import {
  Box,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useMemo, useState } from 'react';
import type { RepoEntry } from './types';

const useStyles = makeStyles(theme => ({
  container: { maxHeight: '60vh' },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  select: { minWidth: 180 },
}));

const ALL = '__all__';

function distinct(values: string[]): string[] {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
}

export function RepoTable({ repos }: { repos: RepoEntry[] }) {
  const classes = useStyles();
  const [query, setQuery] = useState('');
  const [techdocsStatus, setTechdocsStatus] = useState(ALL);
  const [apiSpecStatus, setApiSpecStatus] = useState(ALL);

  const sorted = useMemo(
    () => [...repos].sort((a, b) => a.repo.localeCompare(b.repo)),
    [repos],
  );
  const techdocsOptions = useMemo(
    () => distinct(repos.map(r => r.techdocs?.status ?? 'unknown')),
    [repos],
  );
  const apiSpecOptions = useMemo(
    () => distinct(repos.map(r => r.api_spec?.status ?? 'unknown')),
    [repos],
  );

  const needle = query.trim().toLowerCase();
  const rows = useMemo(
    () =>
      sorted.filter(
        r =>
          (!needle || r.repo.toLowerCase().includes(needle)) &&
          (techdocsStatus === ALL ||
            (r.techdocs?.status ?? 'unknown') === techdocsStatus) &&
          (apiSpecStatus === ALL ||
            (r.api_spec?.status ?? 'unknown') === apiSpecStatus),
      ),
    [sorted, needle, techdocsStatus, apiSpecStatus],
  );

  return (
    <>
      <Box className={classes.filters}>
        {/* Each field carries an id so MUI wires its visible label to the
            control (htmlFor / aria-labelledby); no duplicate aria-labels. */}
        <TextField
          id="context-coverage-repo-filter"
          label="Filter repos"
          size="small"
          variant="outlined"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <TextField
          select
          id="context-coverage-techdocs-status"
          label="TechDocs status"
          size="small"
          variant="outlined"
          className={classes.select}
          value={techdocsStatus}
          onChange={e => setTechdocsStatus(e.target.value)}
        >
          <MenuItem value={ALL}>All</MenuItem>
          {techdocsOptions.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          id="context-coverage-api-spec-status"
          label="API spec status"
          size="small"
          variant="outlined"
          className={classes.select}
          value={apiSpecStatus}
          onChange={e => setApiSpecStatus(e.target.value)}
        >
          <MenuItem value={ALL}>All</MenuItem>
          {apiSpecOptions.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        {/* Live region: screen readers hear the count change as filters do. */}
        <Typography
          variant="body2"
          color="textSecondary"
          role="status"
          aria-live="polite"
        >
          Showing {rows.length} of {repos.length} repos
        </Typography>
      </Box>
      <Paper>
        {/* The container scrolls internally (60vh cap); tabIndex makes it
            reachable and scrollable from the keyboard. */}
        <TableContainer
          className={classes.container}
          tabIndex={0}
          role="region"
          aria-label="Repo list, scrolls vertically"
        >
          <Table
            size="small"
            stickyHeader
            aria-label="Per-repo context coverage"
          >
            <TableHead>
              <TableRow>
                <TableCell>Repo</TableCell>
                <TableCell>Domain</TableCell>
                <TableCell>TechDocs</TableCell>
                <TableCell>API spec</TableCell>
                <TableCell align="right">Declared out / in</TableCell>
                <TableCell align="right">Proposed out / in</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.repo}>
                  <TableCell component="th" scope="row">
                    {r.repo}
                  </TableCell>
                  <TableCell>{r.domain}</TableCell>
                  <TableCell>{r.techdocs?.status ?? 'unknown'}</TableCell>
                  <TableCell>{r.api_spec?.status ?? 'unknown'}</TableCell>
                  <TableCell align="right">
                    {r.dependencies?.declared_out ?? 0} /{' '}
                    {r.dependencies?.declared_in ?? 0}
                  </TableCell>
                  <TableCell align="right">
                    {r.dependencies?.proposed_out ?? 0} /{' '}
                    {r.dependencies?.proposed_in ?? 0}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>No repos match the filters.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}
